import { Injectable, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { doc, setDoc, getDoc, collection, addDoc, updateDoc, deleteDoc, query, where, getDocs, onSnapshot, limit, getCountFromServer } from 'firebase/firestore';
import { User } from '@angular/fire/auth';
import { Observable } from 'rxjs';
import { resolveSmartAgentIcon, resolveSmartAgentBadge, DEFAULT_AGENT_OPTIONS } from '../agent-chat/agent-chat.component';

export interface AppUser {
  uid?: string; // Optional for manually added users before login
  id?: string;  // Firestore ID
  email: string | null;
  displayName: string | null;
  photoURL?: string | null;
  lastLogin?: any;
  lastLoginAt?: any;
  registeredAt?: any;
  role?: string;
  roleId?: string;
  ownerIds?: string[];
}

export interface UserCounts {
  total: number;
  staff: number;
  customers: number;
}

export interface Role {
  id?: string;
  name: string;
  description?: string;
  color?: string;
  isExternal?: boolean;
  isCustomer?: boolean;
}

export interface PagePermission {
  id?: string; // page route or key
  name: string;
  roles: string[];
  showAllCompaniesRoles?: string[]; // list of role names allowed to see all companies on this page
  restrictedRoles?: string[]; // Roles that have page access but are restricted to their assigned ownerIds/companies
  subOptions?: PagePermissionSubOption[];
}

export interface PagePermissionSubOption {
  id: string; // e.g. 'canOverridePaymentStatus'
  name: string; // e.g. 'Can Override Payment Status'
  roles: string[]; // roles that have this sub-permission
  customAction?: string; // for custom renderers like color dropdown
}

export interface ChatAgentSubOption {
  agentId: string;
  name: string;
  icon?: string;
  badge?: string;
  subOptionId: string;
}

export interface PageAccessScope {
  isRestricted: boolean;
  ownerIds: string[];
  companyIds: string[];
  storeIds: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AccessService {
  private firestore = inject(Firestore);

  async saveUserProfile(user: User, customEmail?: string): Promise<boolean> {
    const userRef = doc(this.firestore, 'users', user.uid);
    const docSnap = await getDoc(userRef);

    const now = new Date();
    const existingDocData = docSnap.exists() ? docSnap.data() : null;
    const providerEmail = user.providerData?.find(p => p && p.email)?.email;
    const rawEmail = customEmail || user.email || providerEmail || existingDocData?.['email'] || localStorage.getItem('userEmail') || '';
    const finalEmail = rawEmail.trim().toLowerCase();

    const name = user.displayName || (finalEmail ? finalEmail.split('@')[0] : '');
    const userData: any = {
      uid: user.uid,
      displayName: name,
      name: name,
      photoURL: user.photoURL || null,
      lastLogin: now,
      lastLoginAt: now
    };
    if (finalEmail) {
      userData.email = finalEmail;
    }

    // Always check for other user documents with the same email in the collection (e.g. pre-registered by admin)
    const checkEmail = (user.email || finalEmail).trim().toLowerCase();
    let duplicateAdminDoc: any = null;
    const otherDocsToDelete: any[] = [];

    if (checkEmail) {
      const targetEmailLower = checkEmail;
      const usersRef = collection(this.firestore, 'users');
      const q = query(usersRef, where('email', '==', targetEmailLower), limit(5));
      const querySnapshot = await getDocs(q);

      querySnapshot.docs.forEach(d => {
        if (d.id !== user.uid) {
          const data = d.data();
          // If this other doc has an elevated role or permissions, prioritize it
          if (data['role'] && data['role'].toLowerCase() !== 'user') {
            duplicateAdminDoc = d;
          } else if (!duplicateAdminDoc) {
            duplicateAdminDoc = d;
          }
          otherDocsToDelete.push(d);
        }
      });
    }

    if (docSnap.exists()) {
      // Existing user by UID.
      // If there was an admin-created doc with an elevated role, merge that role in!
      if (duplicateAdminDoc) {
        const adminData = duplicateAdminDoc.data();
        if (adminData['role'] && adminData['role'].toLowerCase() !== 'user') {
          userData.role = adminData['role'];
          if (adminData['roleId']) userData.roleId = adminData['roleId'];
          if (adminData['ownerIds']) userData.ownerIds = adminData['ownerIds'];
          if (adminData['permissions']) userData.permissions = adminData['permissions'];
        }
      }
      if (!existingDocData?.['registeredAt']) {
        userData.registeredAt = existingDocData?.['createdAt'] || duplicateAdminDoc?.data()?.['registeredAt'] || now;
      }
      await setDoc(userRef, userData, { merge: true });

      // Clean up orphaned duplicates
      for (const d of otherDocsToDelete) {
        try {
          await deleteDoc(d.ref);
        } catch (e) {
          console.warn('Failed to delete duplicate user doc:', e);
        }
      }
      return true;
    }

    // Not found by UID. Check if user exists by email.
    if (duplicateAdminDoc) {
      const oldData = duplicateAdminDoc.data();
      const mergedData = { 
        ...oldData, 
        ...userData,
        registeredAt: oldData['registeredAt'] || oldData['createdAt'] || now 
      };

      await setDoc(userRef, mergedData);
      
      for (const d of otherDocsToDelete) {
        try {
          await deleteDoc(d.ref);
        } catch (e) {
          console.warn('Failed to delete duplicate user doc:', e);
        }
      }
      return true;
    }

    // If user is not found, automatically register them into the system with 'User' role
    let assignedRole = 'User';
    let assignedRoleId: string | undefined = undefined;

    try {
      const rolesRef = collection(this.firestore, 'roles');
      const q = query(rolesRef, where('name', 'in', ['User', 'user', 'USER']), limit(1));
      const rolesSnap = await getDocs(q);

      if (!rolesSnap.empty) {
        const userRoleDoc = rolesSnap.docs[0];
        assignedRole = userRoleDoc.data()['name'] || 'User';
        assignedRoleId = userRoleDoc.id;
      }
    } catch (e) {
      console.error('Error fetching role for auto-registration:', e);
    }

    const newUser: any = {
      ...userData,
      role: assignedRole,
      registeredAt: now
    };
    if (assignedRoleId) {
      newUser.roleId = assignedRoleId;
    }

    await setDoc(userRef, newUser);
    return true;
  }

  getUserProfile(uid: string): Observable<AppUser | undefined> {
    const userRef = doc(this.firestore, 'users', uid);
    return new Observable<AppUser | undefined>(observer => {
      return onSnapshot(userRef, (doc) => {
        if (doc.exists()) {
          observer.next({ id: doc.id, ...doc.data() } as AppUser);
        } else {
          observer.next(undefined);
        }
      }, (error) => observer.error(error));
    });
  }

  // === Role Management ===
  getRoles(): Observable<Role[]> {
    const rolesRef = collection(this.firestore, 'roles');
    return new Observable<Role[]>(observer => {
      return onSnapshot(rolesRef, (snap) => {
        const roles = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Role));
        observer.next(roles);
      }, (error) => observer.error(error));
    });
  }

  async addRole(role: Role): Promise<void> {
    const rolesRef = collection(this.firestore, 'roles');
    await addDoc(rolesRef, role);
  }

  async updateRole(role: Role): Promise<void> {
    if (!role.id) return;
    const roleRef = doc(this.firestore, 'roles', role.id);
    await updateDoc(roleRef, { ...role });

    // When role is updated with isCustomer: true, purge from all page permissions
    if (role.isCustomer) {
      try {
        const permsRef = collection(this.firestore, 'page_permissions');
        const snap = await getDocs(permsRef);
        const normRole = (role.name || '').toLowerCase().trim();
        for (const d of snap.docs) {
          const data = d.data() as PagePermission;
          let changed = false;
          let roles = data.roles || [];
          let showAll = data.showAllCompaniesRoles || [];
          if (roles.some(r => r.toLowerCase().trim() === normRole)) {
            roles = roles.filter(r => r.toLowerCase().trim() !== normRole);
            changed = true;
          }
          if (showAll.some(r => r.toLowerCase().trim() === normRole)) {
            showAll = showAll.filter(r => r.toLowerCase().trim() !== normRole);
            changed = true;
          }
          if (changed) {
            await updateDoc(d.ref, { roles, showAllCompaniesRoles: showAll });
          }
        }
      } catch (e) {
        console.warn('Error sanitizing customer role permissions:', e);
      }
    } else if (role.isExternal) {
      // When role is updated with isExternal: true, sanitize non-profit/daily page permissions and purge from showAllCompanies across ALL pages
      try {
        const permsRef = collection(this.firestore, 'page_permissions');
        const snap = await getDocs(permsRef);
        const normRole = (role.name || '').toLowerCase().trim();
        for (const d of snap.docs) {
          const pageId = d.id;
          const data = d.data() as PagePermission;
          let changed = false;
          let roles = data.roles || [];
          let showAll = data.showAllCompaniesRoles || [];

          if (pageId !== 'profit-report' && pageId !== 'daily-report') {
            if (roles.some(r => r.toLowerCase().trim() === normRole)) {
              roles = roles.filter(r => r.toLowerCase().trim() !== normRole);
              changed = true;
            }
          }

          // External roles are strictly scoped to owner companies and can NEVER have showAllCompaniesRoles
          if (showAll.some(r => r.toLowerCase().trim() === normRole)) {
            showAll = showAll.filter(r => r.toLowerCase().trim() !== normRole);
            changed = true;
          }

          if (changed) {
            await updateDoc(d.ref, { roles, showAllCompaniesRoles: showAll });
          }
        }
      } catch (e) {
        console.warn('Error sanitizing external role permissions:', e);
      }
    }
  }

  async getUserOwnerBrandIds(ownerIds: string[]): Promise<string[]> {
    if (!ownerIds || ownerIds.length === 0) return [];
    const brandIdsSet = new Set<string>();
    try {
      const ownershipsCol = collection(this.firestore, 'company_ownerships');
      const companyIdsSet = new Set<string>();
      for (const ownerId of ownerIds) {
        const q = query(ownershipsCol, where('ownerId', '==', ownerId));
        const snap = await getDocs(q);
        snap.forEach(d => {
          const cid = d.data()['companyId'];
          if (cid) companyIdsSet.add(cid.toString().trim());
        });
      }

      if (companyIdsSet.size > 0) {
        const companyCol = collection(this.firestore, 'company');
        const compSnap = await getDocs(companyCol);
        compSnap.forEach(d => {
          if (companyIdsSet.has(d.id.trim())) {
            const data = d.data();
            const bId = data['brand'] || data['brandId'];
            if (bId) brandIdsSet.add(bId.toString().trim());
          }
        });
      }
    } catch (err) {
      console.error('Error resolving user owner brand IDs:', err);
    }
    return Array.from(brandIdsSet);
  }

  async deleteRole(id: string): Promise<void> {
    const roleRef = doc(this.firestore, 'roles', id);
    await deleteDoc(roleRef);
  }

  // === User Management ===
  async getUserCounts(): Promise<UserCounts> {
    try {
      const usersRef = collection(this.firestore, 'users');
      const totalSnap = await getCountFromServer(usersRef);
      const staffQuery = query(usersRef, where('role', 'not-in', ['user', 'User', 'USER']));
      const staffSnap = await getCountFromServer(staffQuery);

      const total = totalSnap.data().count;
      const staff = staffSnap.data().count;
      return {
        total,
        staff,
        customers: Math.max(0, total - staff)
      };
    } catch (err) {
      console.error('Error fetching user counts:', err);
      return { total: 0, staff: 0, customers: 0 };
    }
  }

  getUsers(includeCustomerUsers: boolean = false): Observable<AppUser[]> {
    const usersRef = collection(this.firestore, 'users');
    const q = includeCustomerUsers 
      ? usersRef 
      : query(usersRef, where('role', 'not-in', ['user', 'User', 'USER']));
    return new Observable<AppUser[]>(observer => {
      return onSnapshot(q, (snap) => {
        const users = snap.docs.map(doc => ({ uid: doc.id, ...doc.data() } as AppUser));
        observer.next(users);
      }, (error) => observer.error(error));
    });
  }

  async searchUsersByEmail(email: string): Promise<AppUser[]> {
    const target = (email || '').trim().toLowerCase();
    if (!target) return [];
    const usersRef = collection(this.firestore, 'users');
    const q = query(usersRef, where('email', '==', target), limit(10));
    const snap = await getDocs(q);
    return snap.docs.map(doc => ({ uid: doc.id, ...doc.data() } as AppUser));
  }

  async updateUser(user: AppUser): Promise<void> {
    if (!user.uid) return;
    const userRef = doc(this.firestore, 'users', user.uid);
    await updateDoc(userRef, { ...user });
  }

  async deleteUser(uid: string): Promise<void> {
    const userRef = doc(this.firestore, 'users', uid);
    await deleteDoc(userRef);
  }

  async addUser(user: AppUser): Promise<void> {
    const usersRef = collection(this.firestore, 'users');
    const targetEmailLower = (user.email || '').trim().toLowerCase();
    if (!targetEmailLower) {
      throw new Error('Email address is required.');
    }
    user.email = targetEmailLower;
    const q = query(usersRef, where('email', '==', targetEmailLower), limit(1));
    const snap = await getDocs(q);
    if (!snap.empty) {
      throw new Error(`A user with email "${targetEmailLower}" already exists.`);
    }
    await addDoc(usersRef, {
      ...user,
      registeredAt: new Date()
    });
  }

  // === Page Access Management ===
  getPagePermissions(): Observable<PagePermission[]> {
    const ref = collection(this.firestore, 'page_permissions');
    return new Observable<PagePermission[]>(observer => {
      return onSnapshot(ref, (snap) => {
        const permissions = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as PagePermission));
        observer.next(permissions);
      }, (error) => observer.error(error));
    });
  }

  async updatePagePermission(permission: PagePermission): Promise<void> {
    if (!permission.id) return;
    const ref = doc(this.firestore, 'page_permissions', permission.id);
    await setDoc(ref, permission, { merge: true });
  }

  getChatAgents(): Observable<ChatAgentSubOption[]> {
    const ref = collection(this.firestore, 'agents');
    return new Observable<ChatAgentSubOption[]>(observer => {
      return onSnapshot(ref, (snap) => {
        let list: ChatAgentSubOption[] = snap.docs.map(docSnap => {
          const data = docSnap.data();
          const agentId = docSnap.id;
          return {
            agentId: agentId,
            name: data['name'] || agentId,
            icon: resolveSmartAgentIcon(agentId, data['name'], data['icon']),
            badge: resolveSmartAgentBadge(agentId, data['name'], data['badge']),
            subOptionId: `chat-agent_${agentId}`
          };
        });

        if (list.length === 0) {
          list = DEFAULT_AGENT_OPTIONS.map(d => ({
            agentId: d.agentId,
            name: d.name,
            icon: d.icon,
            badge: d.badge,
            subOptionId: `chat-agent_${d.agentId}`
          }));
        }

        list.sort((a, b) => a.name.localeCompare(b.name));
        observer.next(list);
      }, (error) => observer.error(error));
    });
  }

  // === Email Template Management ===
  async saveEmailTemplate(userId: string, template: string, templateType: string = 'default'): Promise<void> {
      const ref = doc(this.firestore, `users/${userId}/email_templates/${templateType}`);
      await setDoc(ref, { emailTemplate: template, updatedAt: new Date() }, { merge: true });
  }

  async getEmailTemplate(userId: string, templateType: string = 'default'): Promise<string | null> {
      const ref = doc(this.firestore, `users/${userId}/email_templates/${templateType}`);
      const snap = await getDoc(ref);
      return snap.exists() ? snap.data()['emailTemplate'] : null;
  }

  // === SMRS Report Preferences ===
  async getUserSmrsPreferences(userId: string): Promise<any | null> {
    const prefRef = doc(this.firestore, `users/${userId}/preferences/smrs-report`);
    const snap = await getDoc(prefRef);
    return snap.exists() ? snap.data() : null;
  }

  async saveUserSmrsPreferences(userId: string, preferences: any): Promise<void> {
    const prefRef = doc(this.firestore, `users/${userId}/preferences/smrs-report`);
    await setDoc(prefRef, preferences, { merge: true });
  }

  // === Page Access Scope Helper (Show all companies vs owner-restricted) ===
  async getUserPageScope(pageId: string, userProfile?: AppUser | null): Promise<PageAccessScope> {
    if (!userProfile) {
      return { isRestricted: false, ownerIds: [], companyIds: [], storeIds: [] };
    }

    const ownerIds = userProfile.ownerIds || [];

    // Security Invariant: If a user is assigned to specific owner(s), ALWAYS restrict them
    // to their assigned owner(s) and companies, even if their role permits viewing other companies.
    if (ownerIds.length > 0) {
      // 1. Fetch companies linked to user's assigned owners from company_ownerships
      const companyIdsSet = new Set<string>();
      try {
        const ownershipsCol = collection(this.firestore, 'company_ownerships');
        // Query in batches if multiple ownerIds, or individual queries
        for (const ownerId of ownerIds) {
          const q = query(ownershipsCol, where('ownerId', '==', ownerId));
          const snap = await getDocs(q);
          snap.forEach(d => {
            const cid = d.data()['companyId'];
            if (cid) companyIdsSet.add(cid.toString().trim());
          });
        }
      } catch (e) {
        console.error('Error fetching company ownerships for user scope:', e);
      }

      const companyIds = Array.from(companyIdsSet);
      if (companyIds.length === 0) {
        return { isRestricted: true, ownerIds, companyIds: [], storeIds: [] };
      }

      // 2. Fetch stores linked to these companies
      const storeIdsSet = new Set<string>();
      try {
        const storesCol = collection(this.firestore, 'stores');
        const storesSnap = await getDocs(storesCol);
        storesSnap.forEach(d => {
          const data = d.data();
          const sid = (data['storeId'] || d.id || '').toString().trim();
          const cids = data['companyIds'] || (data['companyId'] ? [data['companyId']] : []);
          const belongsToOwner = Array.isArray(cids) && cids.some((cid: any) => companyIdsSet.has(String(cid).trim()));
          if (belongsToOwner && sid) {
            storeIdsSet.add(sid);
          }
        });
      } catch (e) {
        console.error('Error fetching stores for user scope:', e);
      }

      return {
        isRestricted: true,
        ownerIds,
        companyIds,
        storeIds: Array.from(storeIdsSet)
      };
    }

    const userRole = (userProfile.role || '').toLowerCase();
    // Superadmin without assigned ownerIds always has unrestricted access
    if (userRole === 'superadmin') {
      return { isRestricted: false, ownerIds: [], companyIds: [], storeIds: [] };
    }

    // Check page permission config
    try {
      const permRef = doc(this.firestore, 'page_permissions', pageId);
      const permSnap = await getDoc(permRef);
      if (permSnap.exists()) {
        const data = permSnap.data() as PagePermission;
        const showAllRoles = (data.showAllCompaniesRoles || []).map(r => r.toLowerCase());
        
        // If showAllCompaniesRoles is explicitly defined and contains user's role -> unrestricted
        if (showAllRoles.includes(userRole)) {
          return { isRestricted: false, ownerIds: [], companyIds: [], storeIds: [] };
        }
        // If showAllCompaniesRoles is not configured at all (legacy doc), check if role in roles array
        if (!data.showAllCompaniesRoles && data.roles && data.roles.map(r => r.toLowerCase()).includes(userRole)) {
          return { isRestricted: false, ownerIds: [], companyIds: [], storeIds: [] };
        }
      }
    } catch (e) {
      console.warn(`Failed to fetch page permission for ${pageId}:`, e);
    }

    // User has no assigned owners and role does not grant showAllCompanies -> restricted with no companies
    return { isRestricted: true, ownerIds: [], companyIds: [], storeIds: [] };
  }
}
